import { API_CONFIG } from '../config/api';
import type { 
  Unit, 
  CreateUnitData, 
  UpdateUnitData
  // Removed UnitsResponse and UnitResponse
} from '../types/unit';

// Get all units
export const getAllUnits = async (): Promise<Unit[]> => {
  try {
    const response = await fetch(`${API_CONFIG.BASE_URL}/units`, {
      method: 'GET',
      headers: {
        'Accept': 'application/json',
        'Authorization': `Bearer ${localStorage.getItem('token')}`,
      },
    });

    if (!response.ok) {
      const errorText = await response.text();
      console.error('Failed to fetch units:', response.status, errorText);
      throw new Error(`Failed to fetch units: ${response.status}`);
    }

    const result = await response.json();
    
    // Handle different response formats
    if (result.data && Array.isArray(result.data)) {
      return result.data;
    } else if (Array.isArray(result)) {
      return result;
    }
    
    return [];
  } catch (error) {
    console.error('Error fetching units:', error);
    throw new Error('Failed to fetch units');
  }
};

// Get unit by ID
export const getUnitById = async (id: number): Promise<Unit> => {
  try {
    const response = await fetch(`${API_CONFIG.BASE_URL}/units/${id}`, {
      method: 'GET',
      headers: {
        'Accept': 'application/json',
        'Authorization': `Bearer ${localStorage.getItem('token')}`,
      },
    });

    if (!response.ok) {
      const errorText = await response.text();
      console.error('Failed to fetch unit:', response.status, errorText);
      throw new Error(`Failed to fetch unit: ${response.status}`);
    }

    const result = await response.json();
    
    // Handle different response formats
    if (result.data) {
      return result.data;
    }
    
    return result;
  } catch (error) {
    console.error('Error fetching unit:', error);
    throw new Error('Failed to fetch unit');
  }
};

// Create a new unit
export const createUnit = async (unitData: CreateUnitData): Promise<Unit> => {
  try {
    console.log('Creating unit with data:', unitData);

    const response = await fetch(`${API_CONFIG.BASE_URL}/units`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
        'Authorization': `Bearer ${localStorage.getItem('token')}`,
      },
      body: JSON.stringify(unitData),
    });

    if (!response.ok) {
      const errorText = await response.text();
      console.error('Unit creation failed:', response.status, errorText);
      
      let errorMessage = 'Failed to create unit';
      try {
        const errorData = JSON.parse(errorText);
        errorMessage = errorData.message || errorMessage;
      } catch {
        errorMessage = errorText || errorMessage;
      }
      
      throw new Error(errorMessage);
    }

    const result = await response.json();
    console.log('Unit created successfully:', result);
    
    // Handle different response formats
    if (result.data) {
      return result.data;
    }
    
    return result;
  } catch (error) {
    console.error('Error creating unit:', error);
    throw error;
  }
};

// Update a unit
export const updateUnit = async (unitData: UpdateUnitData): Promise<Unit> => {
  try {
    console.log('Updating unit with data:', unitData);

    const response = await fetch(`${API_CONFIG.BASE_URL}/units/${unitData.id}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
        'Authorization': `Bearer ${localStorage.getItem('token')}`,
      },
      body: JSON.stringify({
        name: unitData.name,
        description: unitData.description,
        status: unitData.status,
        code: unitData.code,
      }),
    });

    if (!response.ok) {
      const errorText = await response.text();
      console.error('Unit update failed:', response.status, errorText);
      
      let errorMessage = 'Failed to update unit';
      try {
        const errorData = JSON.parse(errorText);
        errorMessage = errorData.message || errorMessage;
      } catch {
        errorMessage = errorText || errorMessage;
      }
      
      throw new Error(errorMessage);
    }

    const result = await response.json();
    console.log('Unit updated successfully:', result);
    
    // Handle different response formats
    if (result.data) {
      return result.data;
    }
    
    return result;
  } catch (error) {
    console.error('Error updating unit:', error);
    throw error;
  }
};

// Delete a unit
export const deleteUnit = async (id: number): Promise<void> => {
  try {
    const response = await fetch(`${API_CONFIG.BASE_URL}/units/${id}`, {
      method: 'DELETE',
      headers: {
        'Accept': 'application/json',
        'Authorization': `Bearer ${localStorage.getItem('token')}`,
      },
    });

    if (!response.ok) {
      const errorText = await response.text();
      console.error('Unit deletion failed:', response.status, errorText);
      
      let errorMessage = 'Failed to delete unit';
      try {
        const errorData = JSON.parse(errorText);
        errorMessage = errorData.message || errorMessage;
      } catch {
        errorMessage = errorText || errorMessage;
      }
      
      throw new Error(errorMessage);
    }

    console.log('Unit deleted successfully');
  } catch (error) {
    console.error('Error deleting unit:', error);
    throw error;
  }
};